import type {RawStyleBlockType} from '@packages/systems/style-legacy/values';
import type {RawFileType} from '@packages/systems/core/records/FileRecord';
import type {SwatchType} from '@packages/systems/core/utils/StyleTypes';
import type {
  AnyVariableType,
  VariableID,
} from '@packages/systems/core/utils/StyleVariables';
import type {
  ElementPresetAddTabIndex,
  ElementPresetCategory,
  ElementPresetHelpData,
  ElementPresetIcons,
  ElementPresetInteractions,
  ElementPresetIX3,
  ElementPresetNeedsUpgrade,
  ExpressionEscapeHatchFactory,
  ExpressionEscapeHatchFactoryShape,
  ElementPresetEnabled,
} from './ElementPresetTypes';
import {getFirst, getSecond} from '@packages/utilities/Pair';
import {instantiateExpressionWithUniqueIds} from '@packages/utilities/platform-instantiateExpressionWithUniqueIds';
import {identity, emptyArray} from '@packages/utilities/fp/utils';
import {type Expression} from '@packages/systems/wfdl-ast';
import {
  type PluginName,
  type Qualified,
} from '@packages/systems/wfdl-Qualified';
import {AssetName} from '../Asset';

// Opaque type removed - see /packages/utilities/type-safety/README.md for more information
export type ElementPresetName = string;
export const name: (arg1: string) => ElementPresetName = identity;
export const nameToString: (arg1: ElementPresetName) => string = identity;

type UnsafeElementPresetShape = ElementPresetShapeF<
  ExpressionEscapeHatchFactory | Expression
>;
type SafeElementPresetShape = ElementPresetShapeF<Expression>;

// Opaque type removed - see /packages/utilities/type-safety/README.md for more information
/**
 * Opaque alias to UnsafeElementPresetShape
 */
export type UnsafeElementPresetType = UnsafeElementPresetShape;

// Opaque type removed - see /packages/utilities/type-safety/README.md for more information
/**
 * Opaque alias to SafeElementPresetShape
 */
export type SafeElementPresetType = SafeElementPresetShape;

/**
 * Generic element preset type
 */
export type ElementPresetType = SafeElementPresetType | UnsafeElementPresetType;

/**
 * ElementPreset is the data structure used for creating a composition of Elements of various Atom types, for providing pre-defined elements in the AddTab.
 *
 * ```javascript
 * export const elementPresets = {
 *   Animation: ElementPreset.create({
 *     factory: createAnimation, // Uses various Atom Create functions
 *     helpData: {
 *       tag: 'div',
 *       description: ['Load your animations'],
 *       tip: 'Upload the assets yo',
 *       link: {
 *         text: 'Check out the in-depth guide on animation',
 *         href: 'https://university.webflow.com',
 *       },
 *     },
 *     icon: {
 *       small: <AnimationSmall scale={2} />,
 *       medium: <AnimationMedium />,
 *     },
 *     label: 'Animation',
 *     beta: {enabledBasedOnFlags: {betaFlagName: 'lottie'}}
 *   }),
 * };
 * ```
 */
type ElementPresetShapeF<FactoryT> = {
  /**
   * Estimated position of this preset within its category in the AddTab.
   * If omitted, presets are ordererd by plugin name and the order in which
   * they appear in the plugin.
   */
  factory: FactoryT;
  interactions?: ElementPresetInteractions;
  ix3?: ElementPresetIX3;
  /**
   * An Array of `RawStyleBlockType`s to be added that is a serializable JS
   * version of `StyleBlock` (immutablejs Record).
   */
  styleBlocks?: Array<RawStyleBlockType>;
  swatches?: Array<SwatchType>;
  // LEGACY - way to include variables in the element preset where we would include all variables data in each preset
  styleVariables?: Array<AnyVariableType>;
  // CURRENT - desired way to include style variables data in the element preset where we would include only the variable ids in each preset
  styleVariableIds?: Array<Qualified<VariableID>>;
  /**
   * @deprecated Use `assetsIds` instead.
   * LEGACY: An array of `RawFileType`'s to be added. This data is pulled from s3.
   */
  assets?: Array<RawFileType>;
  /**
   *
   * CURRENT: An array of `Qualified<AssetName>`'s to be added.
   */
  assetsIds?: Array<Qualified<AssetName>>;
  /**
   * Name for the AddTab section where the preset will be displayed. A function
   * can be provided to compute the name of the section. If the function returns
   * null the preset will be omitted from the AddTab.
   */
  category?: ElementPresetCategory;
  /**
   * The map of icons displayed in the interface keyed by size.
   */
  icon: ElementPresetIcons;
  /**
   * The label displayed in the interface
   */
  label: string;
  /**
   * Additional terms used only to match this preset when searching for elements.
   */
  searchTerms?: string[];
  /**
   * Indicates if the  elementPreset is dynamic or CMS provided, provides some different styling and help information.
   */
  dynamic?: boolean;
  /**
   * If enabledBasedOnFlags is provided:
   *  - If only one flag is provided, the preset will be enabled if the flag is enabled.
   *  - If both flags are provided, the preset will be enabled if both flags are enabled (AND).
   * If enabled is provided:
   *   - The preset will be enabled if the value is true.
   *   - The preset will be disabled if the value is false.
   *   - This config takes precedence over enabledBasedOnFlags.
   * NOTE: If isNew property of this element is also true, the 'New' badge will take priority over the 'beta' badge.
   */
  beta?: ElementPresetEnabled;

  /**
   * Boolean value to indicate whether to show a 'New' badge on the preset.
   */
  isNew?: boolean;
  /**
   * String value to enable this preset against certain permissions. For example, `enabledIfFlag: 'allowFormFileUpload'`. This is distinct from `beta` as it is relevant to the site's data.
   */
  enableIfFlag?: string;
  /**
   * Object with info to indicate this element preset requires a plan upgrade to be enabled, e.g. Forms, Embed
   */
  needsUpgrade?: ElementPresetNeedsUpgrade;
  /**
   * Estimated position of this preset within its category in the AddTab.
   * A function can also be provided to compute the order index within the category.
   * If omitted or if function returns null, presets are ordered by plugin name and the order in which
   * they appear in the plugin.
   */
  addTabIndex?: ElementPresetAddTabIndex;
  /**
   * Meta UI information for this elementPreset
   */
  helpData?: ElementPresetHelpData;
  /**
   * Image field to be displayed instead of an icon for the Element
   */
  prebuiltLayoutsImage?: string;
};

/**
 * Function used to take an UnsafeElementPresetShape and turn it into a UnsafeElementPresetType.
 */
export function create(
  preset: UnsafeElementPresetShape
): UnsafeElementPresetType {
  return preset;
}

/**
 * Function used to take an `SafeElementPresetShape` and turn it into a `SafeElementPresetType`.
 */
export function createSafe(
  preset: SafeElementPresetShape
): SafeElementPresetType {
  return preset;
}

/**
 * Function to retrieve the `styleBlocks` from an ElementPreset .
 */
export function getStyleBlocks(
  preset: ElementPresetType,
  pluginType: PluginName
): Array<RawStyleBlockType> {
  const blocks = preset.styleBlocks;
  if (!blocks) return emptyArray;
  return blocks.map((block) => ({
    ...block,
    pluginType,
  }));
}

/**
 * @deprecated Use `getAssetsIds` instead.
 * Function to get assets from an element preset.
 */
export function getAssets(
  preset: ElementPresetType
): Array<RawFileType> | null | undefined {
  return preset.assets;
}

/**
 * Function to get assets ids from an element preset.
 */
export function getAssetsIds(
  preset: ElementPresetType
): Array<Qualified<AssetName>> | null | undefined {
  return preset.assetsIds;
}

/**
 * Function to get swatches from an element preset.
 */
export function getSwatches(
  preset: ElementPresetType
): Array<SwatchType> | null | undefined {
  return preset.swatches;
}

/**
 * Function to get style variables (legacy way for storing style variables) from an element preset.
 */
export function getStyleVariables(
  preset: ElementPresetType
): Array<AnyVariableType> | null | undefined {
  return preset.styleVariables;
}

/**
 * Function to get style variables ids from an element preset.
 */
export function getStyleVariableIds(
  preset: ElementPresetType
): Array<Qualified<VariableID>> | null | undefined {
  return preset.styleVariableIds;
}

/**
 * Function to retrieve the `interactions` property from an ElementPreset.
 */
export function getInteractions(
  preset: ElementPresetType
): ElementPresetInteractions | null | undefined {
  return preset.interactions;
}

/**
 * Function to retrieve the `ix3` property from an ElementPreset.
 */
export function getIX3(
  preset: ElementPresetType
): ElementPresetIX3 | null | undefined {
  return preset.ix3;
}

/**
 * Function to retrieve the `dynamic` property from an ElementPreset.
 */
export function getDynamic(
  preset: ElementPresetType
): boolean | null | undefined {
  return preset.dynamic;
}

/**
 * Function to retrieve the `category` property from an ElementPreset.
 */
export function getCategory(
  preset: ElementPresetType
): ElementPresetCategory | null | undefined {
  return preset.category;
}

/**
 * Function to retrieve the `icon` property from an ElementPreset.
 */
export function getIcon(preset: ElementPresetType): ElementPresetIcons {
  return preset.icon;
}

/**
 * Retrieve the `prebuiltLayoutsImage` property from an ElementPreset.
 */
export function getPrebuiltLayoutsImage(
  preset: ElementPresetType
): string | null | undefined {
  return preset.prebuiltLayoutsImage;
}

/**
 * Function to retrieve the `label` property from an ElementPreset.
 */
export function getLabel(preset: ElementPresetType): string {
  return preset.label;
}

/**
 * Returns the text used to index an ElementPreset in element search surfaces.
 * Combines the visible label with any additional `searchTerms` so that
 * presets can be matched by their other names/aliases.
 */
export function getSearchableText(preset: ElementPresetType): string {
  const searchTerms = preset.searchTerms ?? emptyArray;
  return `${getLabel(preset)} ${searchTerms.join(' ')}`.trim();
}

/**
 * Function to retrieve the `beta` property from an ElementPreset.
 */
export function getBeta(
  preset: ElementPresetType
): ElementPresetEnabled | undefined {
  return preset.beta;
}

/**
 * Function to retrieve the `isNew` property from an ElementPreset.
 */
export function getIsNew(
  preset: ElementPresetType
): boolean | null | undefined {
  return preset.isNew;
}

/**
 * Function to retrieve the `enableIfFlag` property from an ElementPreset.
 */
export function getEnableIfFlag(
  preset: ElementPresetType
): string | null | undefined {
  return preset.enableIfFlag;
}

/**
 * Function to retrieve the `needsUpgrade` property from an ElementPreset.
 */
export function getNeedsUpgrade(
  preset: ElementPresetType
): ElementPresetNeedsUpgrade | null | undefined {
  return preset.needsUpgrade;
}

/**
 * Function to retrieve the `addTabIndex` property from an ElementPreset.
 */
export function getAddTabIndex(
  preset: ElementPresetType
): ElementPresetAddTabIndex | null | undefined {
  return preset.addTabIndex;
}

/**
 * Function to retrieve the `helpData` property from an ElementPreset.
 */
export function getHelpData(
  preset: ElementPresetType
): ElementPresetHelpData | null | undefined {
  return preset.helpData;
}

/**
 * Checks if factory is an EscapeHatch function rather than an Expression
 */
export const isExpressionEscapeHatchFactory = (
  preset: ElementPresetType
): boolean => typeof preset.factory === 'function';

/**
 * This function replaces all element IDs in the preset factory with
 * incremented IDs starting from a seed GUID and returns an updated
 * Expression.
 * ```js
 *  const updatedExpr = instantiateFactory(elementPreset, generateGuid()).element;
 * ```
 * @param preset will contain factory property with either Expression or escapeHatch function that returns an Expression
 * @param escapeHatchOptions should only be used if preset has factory that is an escapeHatch function instead of an Expression
 */
export const instantiateFactory = (
  preset: ElementPresetType,
  initialId: string,
  escapeHatchOptions?: ExpressionEscapeHatchFactoryShape
): {
  element: Expression;
  idMap: {
    [key: string]: string;
  };
} => {
  const isEscapeHatch = isExpressionEscapeHatchFactory(preset);

  if (!isEscapeHatch && escapeHatchOptions) {
    throw new TypeError(
      'The elementPreset factory provided is not an escapeHatch function, `escapeHatchOptions` is not required.'
    );
  }

  if (isEscapeHatch && !escapeHatchOptions) {
    throw new TypeError(
      'The elementPreset factory provided utilizes an escapeHatch function but no `escapeHatchOptions` were provided.'
    );
  }

  // ElementPreset is opaque and we don't want to expose factory to the public.
  const presetTypecast = preset as any;
  const factory = presetTypecast.factory;
  let expression: Expression;

  // At this point we know the factory is either an Expression or Expression Escape
  if (isEscapeHatch) {
    expression = factory(escapeHatchOptions);
  } else {
    expression = factory;
  }

  const updatedExpressionWithUniqueIds =
    instantiateExpressionWithUniqueIds(expression)(initialId);

  return {
    element: getSecond(updatedExpressionWithUniqueIds),
    idMap: getFirst(updatedExpressionWithUniqueIds).idMap,
  };
};
